<script setup>
import {ref} from 'vue';
import AccessApplicationCell from '@/components/users/AccessApplicationCell.vue';
import NoResults from '@/components/loaders/NoResults.vue';
import TlDialog from '@/components/TlDialog.vue';

// Admin "applications awaiting your approval" for competition pages
// (tournament / ladder / league cycle) — the HomeTimeline pattern: an
// attention row driven purely by the entity response's
// accessApplicationCount, with the accessApplications list only loaded
// once the review dialog is opened (v-if on the loader), scoped to the
// entity via the filters prop.
const props = defineProps({
  count: {
    type: Number,
    default: 0,
  },
  // e.g. { tournamentIds: [id] } / { ladderIds: [id] } / { leagueCycleIds: [id] }
  filters: {
    type: Object,
    required: true,
  },
});

const dialog = ref(false);
</script>

<template>
  <template v-if="count > 0">
    <!-- Attention row (HomeTimeline accessApplicationAwaitingCount card) -->
    <div class="tl-card tl-attention my-4">
      <v-avatar size="42" color="brandTint">
        <v-icon color="primary">mdi-account-plus-outline</v-icon>
      </v-avatar>
      <div class="tl-attention-who">
        <div class="tl-attention-title">
          {{ count === 1 ? 'Application awaiting your approval' : `${count} applications awaiting your approval` }}
        </div>
        <div class="tl-attention-sub">Review to approve or reject join requests</div>
      </div>
      <v-btn variant="outlined" size="small" rounded="pill" @click="dialog = true">Review</v-btn>
    </div>

    <!-- Review dialog — the list is only fetched once opened -->
    <tl-dialog v-model="dialog" title="Applications awaiting your approval" :max-width="600" flush>
        <crud-list-loader
            v-if="dialog"
            path="api/v2/accessApplications"
            :listeners="['requestsUpdated', 'accessApplicationUpdated']"
            :per-page="50"
            :custom-filters="filters"
        >
          <!-- We already know how many are pending from the entity count, so match
               the skeleton count to avoid a layout jump on load. -->
          <template #loader="{ empty }">
            <template v-if="empty">
              <v-skeleton-loader
                  v-for="n in Math.min(Math.max(count, 1), 50)"
                  :key="n"
                  type="list-item-avatar-two-line"
                  class="mb-2"
              />
            </template>
          </template>
          <template #empty>
            <no-results>No join requests to review.</no-results>
          </template>
          <template #default="{ items: requests }">
            <access-application-cell
                v-for="request in requests"
                :key="request.id"
                :request="request"
            />
          </template>
        </crud-list-loader>
    </tl-dialog>
  </template>
</template>

<style scoped lang="scss">
/* attention card — avatar + who + action (same as HomeTimeline) */
.tl-attention {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;

  .tl-attention-who {
    flex: 1 1 auto;
    min-width: 0;
  }

  .tl-attention-title {
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.3;
  }

  .tl-attention-sub {
    font-size: 11.5px;
    color: rgb(var(--v-theme-textSubtle));
    margin-top: 1px;
  }
}
</style>
